'use client';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { AccountCard } from '@eliadmin/core/src/components/dlz/account/account-card';
import { OrderItemImage } from '@eliadmin/core/src/components/dlz/account/orders/order-parts';
import { usePrice } from '@eliadmin/core/src/components/dlz/account/orders/orders-data';
import { CHECKOUT_COPY } from '@eliadmin/core/src/components/dlz/checkout/checkout-copy';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** „Objem: 100 ml“ of the ordered variant (its attribute values), else the variant's name. */
function variantLine(item: CustomerOrder['items'][number] | undefined) {
  const values = item?.variant?.values ?? [];
  if (values.length) {
    return values
      .map(value => [value.attribute_name, value.name].filter(Boolean).join(': '))
      .join(', ');
  }
  return item?.variant?.name ?? '';
}

type SummaryRow = { key: string; label: string; value: string; tone?: 'success' };

/**
 * Figma „Items“ (60324:20958; phones 60459:92335): 64 px image, name and variant, „quantity × unit
 * price“ and the line total (narrow column: the prices on a row under the name), then subtotal,
 * discount, delivery, payment and the total with VAT. The API has no coupon code: a discount shows
 * as „Zľava“ – the difference between the lines with delivery and payment and the order total.
 */
export function OrderItemsCard({ order, info }: { order: CustomerOrder; info: OrderInfo }) {
  const { t } = useTranslations();
  const price = usePrice();
  const quantity = new Intl.NumberFormat('sk');
  const variants = new Map(order.items.map(item => [item.id, variantLine(item)]));
  const subtotal = info.items.reduce((sum, item) => sum + item.price_with_vat * item.quantity, 0);
  const shipping = info.shipment_price_with_vat;
  const payment = info.payment_type_price_with_vat;
  const discount = subtotal + shipping + payment - info.price_with_vat;
  const charge = (value: number): Pick<SummaryRow, 'value' | 'tone'> =>
    value ? { value: price(value) } : { value: t('text1152'), tone: 'success' };

  const rows: SummaryRow[] = [{ key: 'subtotal', label: t('text675'), value: price(subtotal) }];
  if (discount >= 0.01) {
    rows.push({
      key: 'discount',
      label: CHECKOUT_COPY.summary.couponDiscount,
      value: `−${price(discount)}`,
      tone: 'success',
    });
  }
  if (info.shipment) {
    rows.push({
      key: 'shipping',
      label: `${t('text153')} · ${info.shipment.name}`,
      ...charge(shipping),
    });
  }
  if (info.payment_type) {
    rows.push({
      key: 'payment',
      label: `${t('text158')} · ${info.payment_type.name}`,
      ...charge(payment),
    });
  }

  return (
    <AccountCard title={t('text150')}>
      <ul className="flex flex-col">
        {info.items.map(item => {
          const variant = variants.get(item.id);
          return (
            <li
              key={item.id}
              className="flex flex-col gap-4 border-t border-[#E2E4E7] py-4 first:border-t-0 first:pt-0 last:pb-0 @[40rem]:flex-row @[40rem]:items-center @[40rem]:first:pt-5 @[40rem]:last:pb-4"
            >
              <div className="flex min-w-0 items-center gap-4 @[40rem]:flex-1">
                <OrderItemImage url={item.image?.url} size={64} />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="dlz-body-m-medium text-[#0F1012]">{item.name}</p>
                  {variant ? <p className="dlz-caption text-[#6B7079]">{variant}</p> : null}
                </div>
              </div>
              <div className="flex items-center justify-between gap-4 whitespace-nowrap text-[#0F1012] @[40rem]:contents">
                <p className="dlz-body-s">
                  {quantity.format(item.quantity)} × {price(item.price_with_vat)}
                </p>
                <p className="dlz-h5 text-right @[40rem]:min-w-[100px]">
                  {price(item.price_with_vat * item.quantity)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-col gap-2 border-t border-[#E2E4E7] pt-4 @[40rem]:pt-5">
        <dl className="flex flex-col gap-2">
          {rows.map(row => (
            <div key={row.key} className="dlz-body-s flex items-start gap-3">
              <dt className="min-w-0 flex-1 text-[#4F545C]">{row.label}</dt>
              <dd
                className={cn(
                  'whitespace-nowrap font-semibold',
                  row.tone === 'success' ? 'text-[#16753A]' : 'text-[#0F1012]',
                )}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
        <div className="flex items-end gap-3 text-[#0F1012] @[40rem]:pt-2">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="dlz-h5">{CHECKOUT_COPY.summary.total}</p>
            <p className="dlz-caption text-[#6B7079]">
              {CHECKOUT_COPY.summary.vat} {price(info.price_with_vat - info.price_without_vat)}
            </p>
          </div>
          <p className="dlz-h3 whitespace-nowrap">{price(info.price_with_vat)}</p>
        </div>
      </div>
    </AccountCard>
  );
}
